import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { BUILD_ID } from "@/lib/buildId";
import { awaitFlushWithTimeout, FLUSH_TIMEOUT_MS } from "@/lib/pwaFlush";

const Ctx = createContext(null);
export const usePwaUpdate = () => useContext(Ctx) || {};

const CHECK_THROTTLE_MS = 60 * 1000;        // min gap between checks
const AUTO_INTERVAL_MS = 30 * 60 * 1000;    // periodic check while app stays open

export function PwaUpdateProvider({ children }) {
  const [status, setStatus] = useState("idle"); // idle|checking|downloading|ready|offline|error|uptodate
  const [updateReady, setUpdateReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);
  const [latestBuild, setLatestBuild] = useState(null);
  const [blocked, setBlocked] = useState(false);   // update paused: work couldn't be saved
  const regRef = useRef(null);
  const lastCheckAt = useRef(0);
  const applying = useRef(false);

  const markReady = useCallback(() => {
    setUpdateReady(true);
    setDismissed(false);
    setStatus("ready");
  }, []);

  const watchInstalling = useCallback((reg) => {
    const sw = reg.installing;
    if (!sw) return;
    setStatus("downloading");
    sw.addEventListener("statechange", () => {
      if (sw.state === "installed" && navigator.serviceWorker.controller) markReady();
    });
  }, [markReady]);

  const fetchLatest = useCallback(async () => {
    try {
      const r = await fetch("/version.json", { cache: "no-store" });
      if (r.ok) { const j = await r.json(); setLatestBuild(j.buildId || null); return j.buildId || null; }
    } catch { /* offline / not present */ }
    return null;
  }, []);

  const check = useCallback(async (manual = false) => {
    const now = Date.now();
    if (!manual && now - lastCheckAt.current < CHECK_THROTTLE_MS) return;
    lastCheckAt.current = now;
    if (!navigator.onLine) { setStatus("offline"); return; }
    if (!updateReady) setStatus("checking");
    try {
      const reg = regRef.current || (navigator.serviceWorker ? await navigator.serviceWorker.getRegistration() : null);
      regRef.current = reg;
      const latest = await fetchLatest();
      if (reg) { try { await reg.update(); } catch { /* ignore */ } }
      setLastCheck(new Date());
      if (reg && reg.waiting && navigator.serviceWorker.controller) { markReady(); return; }
      if (reg && reg.installing) { watchInstalling(reg); return; }
      if (updateReady) { setStatus("ready"); return; }
      // A newer published build may exist even if the SW hasn't finished — keep
      // "checking" briefly; the updatefound/statechange handlers flip to ready.
      setStatus(latest && latest !== BUILD_ID ? "downloading" : "uptodate");
    } catch { setStatus("error"); }
  }, [fetchLatest, markReady, watchInstalling, updateReady]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;
    let interval;
    (async () => {
      const reg = await navigator.serviceWorker.getRegistration();
      regRef.current = reg;
      if (reg) {
        if (reg.waiting && navigator.serviceWorker.controller) markReady();
        reg.addEventListener("updatefound", () => watchInstalling(reg));
      }
      check(true);
    })();

    const onVisible = () => { if (document.visibilityState === "visible") check(false); };
    const onOnline = () => check(false);
    const onOffline = () => setStatus("offline");
    const onController = () => {
      // Only reload if WE initiated the update (prevents reload loops and
      // surprise reloads from a background activation).
      if (applying.current || sessionStorage.getItem("psc-update-applying")) {
        sessionStorage.removeItem("psc-update-applying");
        window.location.reload();
      }
    };
    // Respond to the activating worker's "which release are you running?" query
    // so it can retain the exact caches this window still needs.
    const onSwMessage = (event) => {
      const data = event.data || {};
      if (data.type === "WHICH_BUILD" && event.ports && event.ports[0]) {
        event.ports[0].postMessage({ buildId: BUILD_ID });
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    navigator.serviceWorker.addEventListener("controllerchange", onController);
    navigator.serviceWorker.addEventListener("message", onSwMessage);
    interval = setInterval(() => check(false), AUTO_INTERVAL_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      navigator.serviceWorker.removeEventListener("controllerchange", onController);
      navigator.serviceWorker.removeEventListener("message", onSwMessage);
      clearInterval(interval);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyUpdate = useCallback(async () => {
    setBlocked(false);
    const reg = regRef.current || (navigator.serviceWorker ? await navigator.serviceWorker.getRegistration() : null);

    // 1) Ask every open feature (inspection editor, etc.) to durably save its
    //    work and REGISTER a completion promise via detail.waitUntil(). We then
    //    AWAIT those promises — dispatching the event alone is not proof of save.
    const tasks = [];
    try {
      window.dispatchEvent(new CustomEvent("psc:flush-before-update", {
        detail: { waitUntil: (p) => { try { tasks.push(Promise.resolve(p)); } catch { /* noop */ } } },
      }));
    } catch { /* noop */ }

    if (tasks.length > 0) {
      const outcome = await awaitFlushWithTimeout(tasks, FLUSH_TIMEOUT_MS);
      if (outcome !== "ok") {
        // Could not confirm work was saved → DO NOT activate. Show retry.
        setBlocked(true);
        setStatus("error");
        return { ok: false, reason: outcome };
      }
    }

    // 2) Work is safe → activate the waiting worker and reload THIS window once.
    applying.current = true;
    sessionStorage.setItem("psc-update-applying", "1");
    if (reg && reg.waiting) {
      reg.waiting.postMessage({ type: "SKIP_WAITING" });   // controllerchange → reload once
    } else {
      window.location.reload();
    }
    return { ok: true };
  }, []);

  const dismiss = useCallback(() => { setBlocked(false); setDismissed(true); }, []);

  return (
    <Ctx.Provider value={{
      status, updateReady, dismissed, lastCheck, latestBuild, blocked,
      runningBuild: BUILD_ID, check, applyUpdate, dismiss,
    }}>
      {children}
    </Ctx.Provider>
  );
}
